/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountDevicesInitialProperties,
  AccountDevicesResponseInterface,
  RequestAccountDevicesInterFace,
} from '../../interface/account-devices-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchAccountDevices = createAsyncThunk<
  AccountDevicesResponseInterface,
  RequestAccountDevicesInterFace
>(
  'by-transaction/fetchAccountDevices',
  async (data: RequestAccountDevicesInterFace, { dispatch }) => {
    const { accountCustomId, ...query } = data;
    if (!accountCustomId) {
      throw new Error('accountCustomId is required');
    }
    try {
      const response = await accountApi.fetchDeviceListReport(
        accountCustomId,
        removeEmptyKeys(query),
      );
      return response.data.finalResult;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchAccountDevicesFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'by-transaction/fetchAccountDevicesFilters',
  async (_, { dispatch }) => {
    try {
      const response = await accountApi.fetchAccountsListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountDevicesInitialProperties = {
  accountDevices: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const AccountDevicesSlice = createSlice({
  name: 'account-Devices',
  initialState,
  reducers: {
    /**
     * Update By Transaction filters
     * @param state
     * @param action
     */
    updateAccountDevicesFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableAccountDevicesFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountDevicesInitialProperties>) => {
    builder
      .addCase(fetchAccountDevices.pending, (state: AccountDevicesInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAccountDevices.fulfilled,
        (
          state: AccountDevicesInitialProperties,
          action: PayloadAction<AccountDevicesResponseInterface>,
        ) => {
          state.loading = false;
          state.accountDevices = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAccountDevices.rejected, (state: AccountDevicesInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchAccountDevicesFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountDevicesFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchAccountDevicesFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateAccountDevicesFilters, disableAccountDevicesFilterApplied,setSortData } =
  AccountDevicesSlice.actions;
// Export the reducer
export default AccountDevicesSlice.reducer;
